import { useDM } from "@services"
import PropertyInformation from "./propertyInformation"
import { SectionUserDetailsWrapper } from "./styles"
import UserInformation from "./userInformation"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { DMData, DMLiveFeedEventId } from "@store/index"
import { Dispatch, SetStateAction, useEffect, useMemo } from "react"
import { DMDataReservationRequest } from "@store/DM/DMDataReservationRequest"
import Overlay from '../SectionChats/Overlay'


import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { Box, Typography } from "@mui/material"
interface IProps {
	alterationId?: string,
	isAltration: boolean,
	setIsPopupOpen: Dispatch<SetStateAction<boolean>>
	isOverlayOpen?: boolean;
	onCloseOverlay?: () => void
	providerData: {
		provider?: string;
		bookingId?: string;
		liveFeedEventId?: string;
		eventType?: 'reservation_request' | 'inquiry' | 'alteration_request';
	}
	setProviderData: (data: {
		provider?: string;
		bookingId?: string;
		liveFeedEventId?: string;
		eventType?: 'reservation_request' | 'inquiry' | 'alteration_request';
	}) => void;
}

export const DmStatusMapper = (status = '') => {
	let reuslt = {
		en: '',
		ar: '',
		background: '',
		textColor: '',
		pointBg: '#FFE9A9'
	}
	if (status == 'new') {
		reuslt = {
			en: 'confirmed',
			ar: 'مؤكد',
			background: '#22C55E',
			textColor: 'white',
			pointBg: '#16AE26'

		}
	}
	if (status == 'cancelled') {
		reuslt = {
			en: 'cancelled',
			ar: 'ملغي',
			background: '#FFE0E3',
			textColor: '#D70015',
			pointBg: '#D70015'

		}
	}
	if (status == 'reservation_request') {
		reuslt = {
			en: 'pending',
			ar: 'معلق',
			background: '#FFF7E1',
			textColor: '#435363',
			pointBg: '#FFE9A9'

		}
	}
	if (status == 'modified') {
		reuslt = {
			en: 'modified',
			ar: 'معدل',
			background: '#FFF7E1',
			textColor: '#435363',
			pointBg: '#FFE9A9'

		}
	}

	return reuslt

}

export default function SectionUserDetails({ isOverlayOpen, onCloseOverlay = () => { }, setIsPopupOpen, isAltration, alterationId = '', providerData, setProviderData }: IProps) {
	const setDMData = useSetRecoilState(DMData)
	const { GetPropertyDetailsQuery, GetBookingReservationDetailsQuery } = useDM()
	const DMLiveFeedEventIdValue = useRecoilValue(DMLiveFeedEventId)
	const DMDataReservationRequestValue = useRecoilValue(DMDataReservationRequest)

	const DMDataReservationRequestState = useSetRecoilState(
		DMDataReservationRequest
	)

	// Determine which API to call based on provider (memoized for performance)
	const { isBookingComProvider, eventId } = useMemo(() => {
		const isBookingCom = providerData.provider === "BookingCom"
		const id = isAltration 
			? alterationId 
			: isBookingCom 
				? providerData.bookingId 
				: (providerData.liveFeedEventId || DMLiveFeedEventIdValue?.live_feed_event_id)
		
		return { isBookingComProvider: isBookingCom, eventId: id }
	}, [providerData, isAltration, alterationId, DMLiveFeedEventIdValue])

	// Call appropriate API based on provider
	const { data, refetch, isRefetching, isLoading , isSuccess} = isBookingComProvider 
		? GetBookingReservationDetailsQuery(
			{
				eventId: eventId!,
			},
			{ enabled: eventId !== undefined }
		)
		: GetPropertyDetailsQuery(
			{
				eventId: eventId!,
			},
			{ enabled: eventId !== undefined }
		)
	const hideAction = isRefetching || isLoading

	useEffect(() => {
		if (data?.data?.result) {
			const result = data.data.result;
			
			// Update provider data with eventType from API response
			// Only update if eventType actually changed to prevent infinite loop
			if (result?.eventType && result.eventType !== providerData.eventType) {
				setProviderData({
					...providerData,
					eventType: result?.eventType
				});
			}
			
			setDMData({
				guest_name: `${result?.reservation?.guest_first_name || 'guest_first_name'} ${result?.reservation?.guest_last_name || 'guest_last_name'}`,
				checkin_date: dayjs(result?.reservation?.start_date).format(
					"YYYY-MM-DD"
				),
				currency: result?.reservation?.host_currency,
				listing_name: result?.propertyName ?? undefined,
				property_id: result?.listingId,
				room_type_id: "",
				resolved: result?.resolved ?? ""
			})

			DMDataReservationRequestState({
				channals: result?.ota ?? "",
				customer: {
					name: result?.customer?.name ?? "",
					phone: result?.customer?.phone ?? "",
					surname: result?.customer?.surname ?? "",
				},
				reservation: {
					start_date: result?.reservation?.check_in_datetime ?? "",
					end_date: result?.reservation?.check_out_datetime ?? "",
					nightPrice:
						result?.reservation?.expected_payout_amount_accurate ?? "",
					totalPrice:
						result?.reservation?.listing_base_price_accurate ?? "",
					property_id: result?.reservationID ?? "",
					propertyName: result?.propertyName ?? "",
					time_zone: result?.reservation?.time_zone ?? "",
					status_type: result?.eventType ?? "",
					listing_cancellation_payout_accurate: result?.reservation?.listing_cancellation_payout_accurate ?? "",
					listing_cancellation_host_fee_accurate: result?.reservation?.listing_cancellation_host_fee_accurate ?? "",
					expected_payout_amount_before_taxes_accurate: result?.reservation?.expected_payout_amount_before_taxes_accurate ?? "",
					pass_through_tax_amount_paid_to_host_accurate: result?.reservation?.pass_through_tax_amount_paid_to_host_accurate ?? "",
					expected_payout_amount_accurate: result?.reservation?.expected_payout_amount_accurate ?? "",
				},
			})

		} else {
			DMDataReservationRequestState(undefined)
		}
	}, [data, DMLiveFeedEventIdValue])
	const { t,
		i18n: { language },

	} = useTranslation();


	return (
		<SectionUserDetailsWrapper>
			{isSuccess ? (<>
				<Box sx={{
					display: 'flex', justifyContent: 'space-between',

					padding: ' 16px',
					borderBottom: '1px solid #0000001a',
					alignItems: 'center',

				}} >
					<p>{t('DM.Details')}</p>
					{DMDataReservationRequestValue?.reservation?.status_type ? <Box sx={{
						display: 'flex',
						alignItems: 'center',
						backgroundColor: DmStatusMapper(DMDataReservationRequestValue?.reservation?.status_type).background,
						px: '10px',
						height:'30px',

						borderRadius: '50px'
					}} >

						<Box sx={{ 
							borderRadius: '5px',
							width: '9px',
							height: '9px',
							mx: 0.5,
							mt: -0.3,
							backgroundColor: DmStatusMapper(DMDataReservationRequestValue?.reservation?.status_type).pointBg
						}}  >

						</Box>
						<Typography sx={{ color: DmStatusMapper(DMDataReservationRequestValue?.reservation?.status_type).textColor }} >
							{DmStatusMapper(DMDataReservationRequestValue?.reservation?.status_type)[language == "en" ? "en" : "ar"]}
						</Typography>
					</Box> : null}

				</Box>
				<div className='containerInfoData'>
					{eventId && (
						<PropertyInformation
							isOverlayOpen={isOverlayOpen}
							onCloseOverlay={onCloseOverlay}
							onRefresh={() => refetch()} 
							resolved={hideAction ? true : data?.data?.result?.resolved} 
							status={hideAction ? true : data?.data?.result?.eventType} 
							eventType={data?.data?.result?.eventType}
							alterationId={alterationId} 
							isAltration={isAltration} 
							setIsPopupOpen={setIsPopupOpen} />
					)}
				</div>
			</>) : null}
		</SectionUserDetailsWrapper>
	)
}
